// The club roster as one athlete sees it (see `features/athlete-clubs.md`): the
// other members who train at that club, searchable by name, sport or team.
// Rendered inside `MyClubView`, one club at a time.
//
// Every entry is a `ClubMateProfile` — the server's narrow projection. There is
// deliberately no drill-down request: the card shows everything a club-mate is
// allowed to see, so no personal number, exact date of birth, gender or
// attendance figure ever reaches this component to be leaked by a later change.

import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Collapse from '@mui/material/Collapse';
import CardActionArea from '@mui/material/CardActionArea';
import SearchIcon from '@mui/icons-material/Search';
import SportsIcon from '@mui/icons-material/Sports';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import GroupsIcon from '@mui/icons-material/Groups';
import type { ClubMateProfile } from '../../domain';
import { useTranslation } from '../../state/I18nContext';
import { publicProfileUrl } from '../public/profileSharePath';

interface ClubMatesListProps {
  clubMates: ClubMateProfile[];
  /** The club the roster belongs to, for the accessible label of the search box. */
  clubName: string | null;
}

export function ClubMatesList({ clubMates, clubName }: ClubMatesListProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle.length === 0) return clubMates;
    return clubMates.filter((mate) =>
      [
        mate.displayName,
        ...mate.sports.map((s) => s.sportName),
        ...mate.teams.map((team) => team.name),
      ]
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
  }, [clubMates, query]);

  if (clubMates.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <GroupsIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {t('athlete.club.empty')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t('athlete.club.rosterTitle', { count: clubMates.length })}
        </Typography>
        <TextField
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('athlete.club.searchPlaceholder')}
          aria-label={
            clubName
              ? `${t('athlete.club.searchPlaceholder')} — ${clubName}`
              : t('athlete.club.searchPlaceholder')
          }
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: { sm: 240 } }}
        />
      </Stack>

      {matches.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {t('athlete.club.noMatches', { query: query.trim() })}
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(280px, 1fr))' },
            gap: 2,
          }}
        >
          {matches.map((mate) => (
            <Paper key={mate.id} variant="outlined" sx={{ overflow: 'hidden' }}>
              <CardActionArea
                onClick={() => setExpanded((open) => (open === mate.id ? null : mate.id))}
                sx={{ p: 2 }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    src={mate.photoUrls[0] ?? undefined}
                    alt={mate.displayName}
                    sx={{ width: 56, height: 56 }}
                  >
                    {mate.isCoach ? <SportsIcon /> : <DirectionsRunIcon />}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="subtitle1" noWrap title={mate.displayName}>
                      {mate.displayName}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {mate.isCoach && (
                        <Chip size="small" color="secondary" label={t('athlete.club.coach')} />
                      )}
                      {mate.age !== null && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t('athlete.club.age', { count: mate.age })}
                        />
                      )}
                      {mate.sports.map((sport) => (
                        <Chip
                          key={sport.sportId}
                          size="small"
                          variant="outlined"
                          color="info"
                          label={sport.sportName}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </CardActionArea>
              <Collapse in={expanded === mate.id} unmountOnExit>
                <Box sx={{ px: 2, pb: 2 }}>
                  {mate.bio && (
                    <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>
                      {mate.bio}
                    </Typography>
                  )}
                  {mate.teams.length > 0 && (
                    <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                      {mate.teams.map((team, i) => (
                        <Stack key={i} direction="row" spacing={1} alignItems="center">
                          <GroupsIcon color="disabled" fontSize="small" />
                          <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
                            {team.name}
                          </Typography>
                          {team.teamRole && (
                            <Chip size="small" variant="outlined" label={team.teamRole} />
                          )}
                          {team.tier && (
                            <Chip size="small" variant="outlined" label={team.tier} />
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  )}
                  {mate.photoUrls.length > 1 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                      {mate.photoUrls.slice(1).map((url) => (
                        <Box
                          key={url}
                          component="img"
                          src={url}
                          alt={mate.displayName}
                          loading="lazy"
                          sx={{
                            width: 72,
                            height: 72,
                            objectFit: 'cover',
                            borderRadius: 1,
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                  {/* Only offered when the club has actually published the page —
                      `isPublic` is the server's own gate, mirrored here. */}
                  {mate.isPublic && (
                    <Typography
                      variant="body2"
                      component="a"
                      href={publicProfileUrl(mate.id, window.location.origin)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('athlete.club.openPublicPage')}
                    </Typography>
                  )}
                  {!mate.bio && mate.teams.length === 0 && mate.photoUrls.length <= 1 && (
                    <Typography variant="body2" color="text.secondary">
                      {t('athlete.club.noDetails')}
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}
